import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, Loader2, LogOut } from "lucide-react";
import { toast, Toaster } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { LeadDetail, ROLE_LABELS, STATUS_META, STATUSES, formatDate } from "../../components/crm/LeadDetail";
import { IMAGES } from "../../data/content";
import {
  crmChangePassword,
  crmLeads,
  crmLogout,
  crmMe,
  crmStats,
  crmUpdateLead,
  formatApiError,
} from "../../lib/crm";

export default function CrmDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [pwOpen, setPwOpen] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "" });
  const [pwLoading, setPwLoading] = useState(false);

  const load = useCallback(async () => {
    const [s, l] = await Promise.all([crmStats(), crmLeads()]);
    setStats(s.data);
    setLeads(l.data);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const meRes = await crmMe();
        setUser(meRes.data);
        await load();
      } catch {
        navigate("/crm/login");
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate, load]);

  const moveStatus = async (lead, status) => {
    try {
      await crmUpdateLead(lead.id, { status });
      toast.success(`${lead.name} → ${STATUS_META[status].label}`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const handleLogout = async () => {
    try { await crmLogout(); } catch {}
    navigate("/crm/login");
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwLoading(true);
    try {
      await crmChangePassword(pw.current, pw.next);
      toast.success("Senha alterada com sucesso.");
      setPwOpen(false);
      setPw({ current: "", next: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setPwLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream">
        <Loader2 className="h-8 w-8 animate-spin text-teal" />
      </div>
    );
  }

  const cards = stats
    ? [
        ["Total de candidaturas", stats.total],
        ["Novas (7 dias)", stats.novos_7d],
        ["Miss Aura", stats.por_role.MISS_AURA],
        ["Embaixadoras", stats.por_role.EMBAIXADORA],
        ["Em análise", stats.por_status.EM_ANALISE],
        ["Aprovadas", stats.por_status.APROVADA],
        ["Reprovadas", stats.por_status.REPROVADA],
      ]
    : [];

  return (
    <div className="min-h-screen bg-cream" data-testid="crm-dashboard">
      <Toaster position="top-center" richColors />
      <header className="border-b border-gold-border bg-creamalt">
        <div className="mx-auto flex max-w-[1600px] items-center justify-between px-5 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <img src={IMAGES.logo} alt="Logo Ministério AURA" className="h-10 w-10 rounded-full object-cover" />
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-gold">Área Administrativa</p>
              <h1 className="font-serif text-xl text-ink">CRM — Candidaturas AURA</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden text-xs text-mutedink sm:block">{user?.email}</span>
            <button
              data-testid="crm-change-password-btn"
              onClick={() => setPwOpen(true)}
              title="Alterar senha"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-gold-border text-mutedink transition-colors hover:bg-teal hover:text-white"
            >
              <KeyRound className="h-4 w-4" />
            </button>
            <button
              data-testid="crm-logout-btn"
              onClick={handleLogout}
              title="Sair"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-gold-border text-mutedink transition-colors hover:bg-teal hover:text-white"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1600px] px-5 py-8 sm:px-6">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-7" data-testid="crm-stats">
          {cards.map(([label, value]) => (
            <div key={label} className="rounded-2xl border border-gold-border bg-white p-4">
              <p className="font-serif text-3xl text-teal">{value}</p>
              <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-mutedink">{label}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex gap-4 overflow-x-auto pb-4" data-testid="crm-kanban">
          {STATUSES.map((status) => {
            const items = leads.filter((l) => l.status === status);
            return (
              <div key={status} data-testid={`crm-column-${status.toLowerCase()}`} className="w-72 shrink-0">
                <div className={`flex items-center justify-between rounded-t-2xl border px-4 py-3 ${STATUS_META[status].classes}`}>
                  <span className="text-[10px] font-semibold uppercase tracking-[0.2em]">{STATUS_META[status].label}</span>
                  <span className="font-serif text-lg">{items.length}</span>
                </div>
                <div className="space-y-3 rounded-b-2xl border border-t-0 border-gold-border bg-creamalt/60 p-3 min-h-[120px]">
                  {items.map((lead) => (
                    <div
                      key={lead.id}
                      data-testid={`crm-lead-card-${lead.id}`}
                      onClick={() => setDetail(lead)}
                      className="cursor-pointer rounded-xl border border-gold-border bg-white p-4 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_30px_rgba(197,160,89,0.15)]"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-serif text-lg leading-tight text-ink">{lead.name}</p>
                        <span className="shrink-0 rounded-full bg-teal-light px-2.5 py-1 text-[9px] font-semibold uppercase tracking-[0.15em] text-teal-deep">
                          {ROLE_LABELS[lead.role] || lead.role}
                        </span>
                      </div>
                      {lead.city && <p className="mt-1 text-xs text-mutedink">{lead.city}</p>}
                      <p className="mt-1 text-[10px] text-mutedink">{formatDate(lead.created_at)}</p>
                      {(lead.referral_code || lead.preferred_miss_aura_code) && (
                        <p className="mt-2 rounded-full bg-gold-light px-2.5 py-1 text-[9px] font-semibold uppercase tracking-[0.15em] text-gold w-fit">
                          {lead.preferred_miss_aura_code ? `Convite ${lead.preferred_miss_aura_code}` : `Ref ${lead.referral_code}`}
                        </p>
                      )}
                      <select
                        data-testid={`crm-status-select-${lead.id}`}
                        value={lead.status}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => moveStatus(lead, e.target.value)}
                        className="mt-3 w-full rounded-lg border border-gold-border bg-cream px-2 py-1.5 text-[10px] font-semibold uppercase tracking-[0.1em] text-mutedink outline-none focus:border-teal"
                      >
                        {STATUSES.map((s) => (
                          <option key={s} value={s}>{STATUS_META[s].label}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                  {items.length === 0 && (
                    <p className="py-6 text-center text-xs text-mutedink/60">Nenhuma candidatura</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </main>

      <LeadDetail
        lead={detail}
        open={!!detail}
        onOpenChange={(open) => !open && setDetail(null)}
        onUpdated={(updated) => { setDetail(updated); load(); }}
        onDeleted={() => { setDetail(null); load(); }}
      />

      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent className="max-w-sm border-gold-border bg-cream" data-testid="crm-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-ink">Alterar senha</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="mt-4 space-y-4">
            <input
              data-testid="crm-pw-current"
              type="password"
              required
              value={pw.current}
              onChange={(e) => setPw((p) => ({ ...p, current: e.target.value }))}
              placeholder="Senha atual"
              className="w-full rounded-xl border border-gold-border bg-white px-4 py-3 text-sm text-ink outline-none focus:border-teal"
            />
            <input
              data-testid="crm-pw-new"
              type="password"
              required
              value={pw.next}
              onChange={(e) => setPw((p) => ({ ...p, next: e.target.value }))}
              placeholder="Nova senha (mín. 8 caracteres)"
              className="w-full rounded-xl border border-gold-border bg-white px-4 py-3 text-sm text-ink outline-none focus:border-teal"
            />
            <button
              data-testid="crm-pw-submit"
              type="submit"
              disabled={pwLoading}
              className="w-full rounded-full bg-teal py-3 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-teal-hover disabled:opacity-60"
            >
              {pwLoading ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : "Salvar nova senha"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
